import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProgramsPage = () => {
  const [programs, setPrograms] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await axios.get(`${API}/programs`);
        setPrograms(response.data);
      } catch (error) {
        console.error("Error fetching programs:", error);
      }
    };
    fetchPrograms();
  }, []);

  const filteredPrograms = filter === "all" 
    ? programs 
    : programs.filter(p => p.level.includes(filter));

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero */}
        <section className="py-16 md:py-24" data-testid="programs-hero">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <h1 className="font-oswald text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tighter mb-6 animate-fade-in">
              ALL PROGRAMS
            </h1>
            <p className="font-manrope text-neutral-400 text-lg max-w-2xl mx-auto mb-10">
              Explore our complete range of fitness programs. Each one is designed to deliver 
              results through proven training methodologies.
            </p>
            <Link to="/assessment" data-testid="find-program-cta">
              <button className="btn-primary">
                FIND YOUR PERFECT PROGRAM
              </button>
            </Link>
          </div>
        </section>

        {/* Filter */}
        <section className="pb-8 section-divider">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-wrap justify-center gap-4 pt-8">
              {["all", "beginner", "intermediate", "advanced"].map((level) => (
                <button
                  key={level}
                  onClick={() => setFilter(level)}
                  className={`font-oswald uppercase tracking-wider text-sm px-6 py-3 transition-all duration-200 ${
                    filter === level 
                      ? 'bg-white text-black' 
                      : 'border border-neutral-700 text-neutral-400 hover:border-white hover:text-white'
                  }`}
                  data-testid={`filter-${level}`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Programs Grid */}
        <section className="py-16" data-testid="programs-grid">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPrograms.map((program) => (
                <Link 
                  key={program.id}
                  to={`/results/${program.id}`}
                  className="card-hover bg-neutral-950 overflow-hidden group block"
                  data-testid={`program-${program.id}`}
                >
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={program.image}
                      alt={program.name}
                      className="w-full h-full object-cover img-grayscale group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/40"></div>
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className="font-oswald text-xs uppercase tracking-wider bg-white text-black px-3 py-1">
                        {program.duration}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-oswald text-2xl font-bold uppercase tracking-tighter mb-3">
                      {program.name}
                    </h3>
                    <p className="font-manrope text-neutral-500 mb-4">
                      {program.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {program.features.slice(0, 3).map((feature, i) => (
                        <span key={i} className="text-xs text-neutral-400 border border-neutral-700 px-2 py-1">
                          {feature}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        {program.level.split(",").map((lvl, i) => (
                          <span key={i} className="text-xs text-neutral-500 uppercase">
                            {lvl.trim()}
                          </span>
                        ))}
                      </div>
                      <span 
                        className="font-oswald text-sm uppercase tracking-wider text-white hover:text-neutral-400 transition-colors flex items-center gap-2"
                        data-testid={`view-program-${program.id}`}
                      >
                        VIEW
                        <ArrowRight size={16} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredPrograms.length === 0 && (
              <div className="text-center py-16">
                <p className="font-manrope text-neutral-500 text-lg">
                  No programs found for this filter.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-neutral-950 section-divider">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-6">
              NOT SURE WHICH PROGRAM<br />IS RIGHT FOR YOU?
            </h2>
            <p className="font-manrope text-neutral-400 text-lg max-w-2xl mx-auto mb-10">
              Take our quick assessment and we'll match you with the perfect program 
              based on your goals, fitness level, and preferences.
            </p>
            <Link to="/assessment" data-testid="assessment-cta-bottom">
              <button className="btn-primary flex items-center justify-center gap-2 mx-auto">
                START ASSESSMENT
                <ArrowRight size={20} />
              </button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ProgramsPage;
